<script lang="ts">
    import type {CriterionsFormManagerState} from '../search-criteria/criterions-form-manager';
    import type {SearchFormPageData, SearchStringsState} from './types';
    import type {SearchHistoryEntryDto} from 'typings/portaro.be.types';
    import type {Subscription} from 'rxjs';
    import {onDestroy, onMount} from 'svelte';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import {getInjector} from 'core/svelte-context/context';
    import {KpSearchFormService} from './kp-search-form.service';
    import {KpSearchFormManager} from './kp-search-form.manager';
    import {unsubscribeAllSubscriptions} from 'shared/utils/observables-utils';
    import KpLoadablePageContainer from 'shared/layouts/KpLoadablePageContainer.svelte';
    import SearchFormHeadingPart from './parts/SearchFormHeadingPart.svelte';
    import KpSearchHistoryPanel from '../kp-search-history-panel/KpSearchHistoryPanel.svelte';
    import KpVisitedPanel from '../../record/kp-visited-documents-panel/KpVisitedPanel.svelte';
    import KpFavouritesPanel from '../../favourites/kp-favourites-panel/KpFavouritesPanel.svelte';
    import SearchFormFormPart from './parts/SearchFormFormPart.svelte';

    const service = getInjector().getByToken<KpSearchFormService>(KpSearchFormService.serviceName);
    let manager: KpSearchFormManager;

    let loading = true;
    let pageData: SearchFormPageData;
    let fillToFormUnregister: () => void;
    $: loadError = !loading && (!exists(pageData) || !exists(manager));

    let expertSearch = false;
    let criterionsState: CriterionsFormManagerState;
    let searchStringsState: SearchStringsState;
    let criterionsStateSubscription: Subscription;
    let searchStringsStateSubscription: Subscription;

    onMount(async () => {
        pageData = await service.getPageData();
        manager = new KpSearchFormManager(pageData, service);
        await manager.initialize();

        criterionsStateSubscription = manager.getCriterionsState$().subscribe((state) => criterionsState = state);
        searchStringsStateSubscription = manager.getSearchStringsState$().subscribe((state) => searchStringsState = state);
        fillToFormUnregister = service.registerSearchHistoryPanelFillToFormEventListener(handleFillToForm);

        loading = false;
    });

    onDestroy(() => {
        if (isFunction(fillToFormUnregister)) {
            fillToFormUnregister();
        }

        unsubscribeAllSubscriptions(criterionsStateSubscription, searchStringsStateSubscription);
    });

    const handleFillToForm = (event: CustomEvent<SearchHistoryEntryDto>) => {
        manager.useRestriction(event.detail.qt);
    }

    const handleFormReset = async () => {
        await manager.resetForm(expertSearch);
    }

    const handleToggleExpertSearch = (event: CustomEvent<boolean>) => {
        if (expertSearch === event.detail) {
            return;
        }

        expertSearch = event.detail;
        manager.fillCriterions(event.detail);
    }
</script>

<KpLoadablePageContainer pageClass="kp-search-form-page search search-form-simple"
                         {loadError}
                         {loading}>

    <div class="row">
        <div class="col-sm-8">
            <SearchFormHeadingPart showResetFormButton="{criterionsState.isAnyCriterionFilled}"
                                   on:reset={handleFormReset}
                                   on:toggle-expert-search={handleToggleExpertSearch}/>

            <SearchFormFormPart {manager}
                                {expertSearch}
                                {criterionsState}
                                {searchStringsState}/>
        </div>

        <aside class="col-sm-4">
            <KpSearchHistoryPanel/>
            <KpFavouritesPanel/>
            <KpVisitedPanel/>
        </aside>
    </div>
</KpLoadablePageContainer>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .row {
        margin-left: -15px;
        margin-right: -15px;
        width: calc(100% + 30px);

        .col-sm-8,
        .col-sm-4 {
            display: flex;
            flex-direction: column;
            gap: 30px;
        }

        @media (max-width: @screen-xs-max) {
            display: flex;
            flex-direction: column;
            gap: 30px;
        }
    }
</style>